import { SourcingStats } from 'api-clients/monolith';
import { format } from 'date-fns';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import ExclamationCircle from '@/assets/icons/exclamation-circle.svg';
import { Typography, TypographyVariant } from '@/components/ui/typography';
import { useHireUrl } from '@/hooks/use-hire-url';
import { getProgress } from '@/utils/progress';

import { Button } from './button';
import { Progress } from './progress';
import { Tooltip } from './tooltip';

interface DurationProps {
  stats: SourcingStats;
  brand?: string;
  location?: string;
  funnel?: string;
  showProgress?: boolean;
  typographyVariant?: TypographyVariant;
  showDays?: boolean;
  showDateRange?: boolean;
  startDate?: string;
  endDate?: string;
  tooltipContent?: ReactNode;
  showNewGoalButton?: boolean;
  hiringGoalAutomationEnabled?: boolean;
}

export function Duration({
  stats,
  brand,
  location,
  funnel,
  showProgress = false,
  typographyVariant = 'text',
  showDays = true,
  showDateRange = false,
  startDate,
  endDate,
  tooltipContent,
  showNewGoalButton = false,
  hiringGoalAutomationEnabled,
}: DurationProps) {
  const { getHireATHiringGoalUrl } = useHireUrl();
  const { expected, actual } = stats;
  const progress = getProgress(expected, actual);
  const intl = useIntl();

  function NewGoalLinkButton() {
    return (
      <Button variant="link" asChild>
        <Link
          to={getHireATHiringGoalUrl(brand ?? 'no_brand', location, funnel)}
          target="_blank"
        >
          <FormattedMessage
            defaultMessage="+ New goal"
            description="Link beside duration when user should add a hire-by goal in Hire"
          />
        </Link>
      </Button>
    );
  }

  if (showNewGoalButton) return <NewGoalLinkButton />;

  const renderContent = () => {
    if (showDateRange && startDate && endDate) {
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`;
    }

    if (!expected && !actual) return <>- / -</>;

    if (showDays) {
      const daysLeft =
        expected != null && actual != null ? expected - actual : null;
      const showWarning = daysLeft === 1 && !hiringGoalAutomationEnabled;

      return (
        <>
          <div className="flex h-[16px] w-full items-center gap-1">
            <FormattedMessage
              defaultMessage="{actual} / {expected} days"
              values={{ actual: actual ?? '-', expected: expected ?? '-' }}
              description="Elapse vs target window: {actual} and {expected} are day counts"
            />
            {showWarning && (
              <Tooltip
                content={intl.formatMessage({
                  defaultMessage:
                    'The hiring goal for this opening ends in 1 day. Consider extending the end date of your goal or make changes to your campaigns reach your goal.',
                  description:
                    'The hiring goal for this opening ends in 1 day. Consider extending the end date of your goal or make changes to your campaigns reach your goal.',
                })}
              >
                <img
                  src={ExclamationCircle}
                  alt="Ends in 1 day"
                  className="ml-auto h-[16px] w-auto"
                />
              </Tooltip>
            )}
          </div>
        </>
      );
    }

    return `${actual ?? '-'} / ${expected ?? '-'}`;
  };

  return (
    <>
      <Tooltip content={tooltipContent}>
        <Typography variant={typographyVariant}>{renderContent()}</Typography>
      </Tooltip>
      {showProgress && <Progress value={progress} />}
    </>
  );
}
